import { api } from "./api";

export type AssetClass = "LIQUIDE" | "ANLAGEN" | "IMMOBILIE" | "SONSTIGE" | "VERBINDLICHKEITEN";
export type NetworthSource = "DERIVED" | "MANUAL" | "BACKFILL";

export interface ClassRow {
  valueCents: number;
  source: NetworthSource;
  note: string | null;
}

export interface SnapshotDetail {
  stichtag: string;
  totalCents: number;
  classes: Record<AssetClass, ClassRow>;
  createdAt: string;
}

export interface SnapshotListItem {
  stichtag: string;
  totalCents: number;
  createdAt: string;
}

export interface SnapshotList {
  snapshots: SnapshotListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface NetworthSummary {
  latest: { stichtag: string; totalCents: number } | null;
  daysSinceLastSnapshot: number | null;
  isStale: boolean;
}

export interface NetworthPreview {
  stichtag: string;
  derived: {
    LIQUIDE: { valueCents: number; source: "DERIVED" };
    ANLAGEN: { valueCents: number; source: "DERIVED" };
    VERBINDLICHKEITEN: { valueCents: number; source: "DERIVED" };
  };
}

export interface CreateSnapshotInput {
  stichtag: string;
  manual?: {
    IMMOBILIE?: { valueCents: number; note?: string };
    SONSTIGE?: { valueCents: number; note?: string };
  };
  overrides?: {
    LIQUIDE?: { valueCents: number; note?: string };
    ANLAGEN?: { valueCents: number; note?: string };
    VERBINDLICHKEITEN?: { valueCents: number; note?: string };
  };
}

export interface ListSnapshotsFilters {
  limit?: number;
  offset?: number;
}

export interface HistoryRow {
  stichtag: string;
  classes: {
    LIQUIDE: number;
    ANLAGEN: number;
    IMMOBILIE: number;
    SONSTIGE: number;
    VERBINDLICHKEITEN: number;
  };
  totalNetCents: number;
}

export type ExcludablePositionSource = "INVESTMENT" | "INSURANCE";

export interface ExcludablePosition {
  id: string;
  source: ExcludablePositionSource;
  name: string;
  kind: string;
  classKey: AssetClass;
  perStichtag: Record<string, number>;
}

export interface NetworthHistory {
  history: HistoryRow[];
  excludablePositions: ExcludablePosition[];
}

export interface SnapshotDrillTopMover {
  investmentId: string;
  name: string;
  symbol: string;
  kind: string;
  classKey: "ANLAGEN" | "SONSTIGE";
  deltaEur: number;
  deltaPercent: number | null;
  note: string | null;
}

export interface SnapshotDrillResponse {
  stichtag: string;
  classes: Record<"LIQUIDE" | "ANLAGEN" | "IMMOBILIE" | "SONSTIGE" | "VERBINDLICHKEITEN", number>;
  prevStichtag: string | null;
  prevClasses:
    | Record<"LIQUIDE" | "ANLAGEN" | "IMMOBILIE" | "SONSTIGE" | "VERBINDLICHKEITEN", number>
    | null;
  daysSincePrev: number | null;
  topMovers: SnapshotDrillTopMover[];
}

function qs(filters: ListSnapshotsFilters): string {
  const entries: [string, string][] = [];
  if (filters.limit !== undefined) entries.push(["limit", String(filters.limit)]);
  if (filters.offset !== undefined) entries.push(["offset", String(filters.offset)]);
  if (entries.length === 0) return "";
  return `?${new URLSearchParams(entries).toString()}`;
}

export const networthApi = {
  listSnapshots: (filters: ListSnapshotsFilters = {}) =>
    api<SnapshotList>(`/networth/snapshots${qs(filters)}`),
  getSnapshotDetail: (stichtag: string) =>
    api<SnapshotDetail>(`/networth/snapshots/${encodeURIComponent(stichtag)}`),
  createSnapshot: (input: CreateSnapshotInput) =>
    api<SnapshotDetail>("/networth/snapshots", { method: "POST", body: input }),
  deleteSnapshot: (stichtag: string) =>
    api<void>(`/networth/snapshots/${encodeURIComponent(stichtag)}`, { method: "DELETE" }),
  getSummary: () => api<NetworthSummary>("/networth/summary"),
  getPreview: (stichtag: string) =>
    api<NetworthPreview>(`/networth/preview?stichtag=${encodeURIComponent(stichtag)}`),
  async getHistory(filters: { from?: string; to?: string }): Promise<NetworthHistory> {
    const params = new URLSearchParams();
    if (filters.from) params.set("from", filters.from);
    if (filters.to) params.set("to", filters.to);
    const qstr = params.toString();
    return api<NetworthHistory>(`/networth/history${qstr ? `?${qstr}` : ""}`);
  },
  backfillHistorical: (input: BackfillHistoricalInput) =>
    api<BackfillHistoricalAck>("/networth/snapshots/backfill-historical", {
      method: "POST",
      body: input,
    }),
  getBackfillStatus: () =>
    api<BackfillJobState>("/networth/snapshots/backfill-status"),
  cleanupZero: () =>
    api<{ deletedDays: number }>("/networth/snapshots/cleanup-zero", { method: "POST" }),
  getSnapshotDrill: (stichtag: string) =>
    api<SnapshotDrillResponse>(
      `/networth/snapshots/detail?stichtag=${encodeURIComponent(stichtag)}`,
    ),
};

export interface BackfillHistoricalInput {
  fromDate?: string;
  untilDate?: string;
  intervalDays?: number;
  force?: boolean;
}

export interface BackfillHistoricalResult {
  processed: number;
  daysFrom: string;
  daysUntil: string;
  intervalDays: number;
}

export interface BackfillHistoricalAck {
  status: "started" | "already_running";
}

export type BackfillJobState =
  | { status: "idle" }
  | {
      status: "running";
      startedAt: string;
      processedDays: number;
      totalDays: number;
    }
  | {
      status: "completed";
      startedAt: string;
      completedAt: string;
      processedDays: number;
      totalDays: number;
      daysFrom: string;
      daysUntil: string;
    }
  | {
      status: "failed";
      startedAt: string;
      completedAt: string;
      error: string;
    };
